import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/feature/Pagination";
import FilterBar from "./components/FilterBar";
import { allGenres, regions } from "@/mocks/movies";
import {
  filterMovies,
  formatViews,
  getPopularMovies,
  sortMovies,
  type SortKey,
} from "@/utils/movies";

const PAGE_SIZE = 12;

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const region = searchParams.get("region") || "全部";
  const genre = searchParams.get("genre") || "全部";
  const year = searchParams.get("year") || "全部";
  const sort = (searchParams.get("sort") as SortKey) || "updated";
  const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10) || 1);

  const updateParams = (patch: Record<string, string>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (!value || value === "全部" || (key === "sort" && value === "updated")) {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    });
    if (!("page" in patch)) next.delete("page");
    setSearchParams(next);
  };

  const result = useMemo(() => {
    const filtered = filterMovies({ region, year, genre });
    return sortMovies(filtered, sort);
  }, [region, year, genre, sort]);

  const totalPages = Math.max(1, Math.ceil(result.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = result.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const categoryName =
    region !== "全部" ? `${region}伦理片` : genre !== "全部" ? `${genre}类影片` : "全部影片";

  const description =
    region !== "全部"
      ? `汇集优质的${region}伦理剧情电影，涵盖剧情、爱情、悬疑等多种类型，按年份与口碑自由筛选。`
      : genre !== "全部"
        ? `精选${genre}类型的日韩伦理剧情作品，梳理剧情脉络与人物关系，方便你快速选片。`
        : "浏览全部日韩伦理剧情影片，支持按地区、年份、类型筛选与多种排序方式。";

  const popular = getPopularMovies(6);

  const resetFilters = () => setSearchParams(new URLSearchParams());

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-8 md:py-8">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "分类" }]} />

      <header className="mt-4">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          【{categoryName}】- 日韩伦理片
        </h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">{description}</p>
      </header>

      <div className="mt-6 flex flex-col gap-6 lg:flex-row">
        <div className="min-w-0 flex-1">
          <FilterBar
            region={region}
            genre={genre}
            year={year}
            sort={sort}
            resultCount={result.length}
            onChange={(patch) =>
              updateParams(Object.fromEntries(Object.entries(patch).map(([k, v]) => [k, String(v)])))
            }
            onReset={resetFilters}
          />

          {pageItems.length === 0 ? (
            <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-emotion-sad-line text-3xl" />
              </span>
              <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
                没有找到符合条件的影片
              </h2>
              <p className="mt-2 max-w-md text-sm text-foreground-600">
                当前筛选条件过于严格，试试放宽地区、年份或类型，重新发现好片。
              </p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-refresh-line" />
                重置筛选条件
              </button>
            </div>
          ) : (
            <>
              <div className="mt-5 grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-3">
                {pageItems.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} showSummary />
                ))}
              </div>
              <Pagination
                currentPage={safePage}
                totalPages={totalPages}
                onPageChange={(p) => {
                  updateParams({ page: String(p) });
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              />
            </>
          )}
        </div>

        <aside className="w-full lg:w-[300px]">
          <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
              <i className="ri-price-tag-3-line text-primary-600" />
              热门标签
            </h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {regions.map((r) => (
                <Link
                  key={r}
                  to={`/category?region=${encodeURIComponent(r)}`}
                  className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800 transition-colors hover:bg-secondary-200"
                >
                  {r}
                </Link>
              ))}
              {allGenres.map((g) => (
                <Link
                  key={g}
                  to={`/category?genre=${encodeURIComponent(g)}`}
                  className="rounded-full bg-background-100 px-3 py-1 text-xs font-medium text-foreground-700 transition-colors hover:bg-accent-100 hover:text-accent-800"
                >
                  {g}
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
              <i className="ri-fire-line text-primary-600" />
              高分推荐
            </h2>
            <ol className="mt-3 flex flex-col">
              {popular.map((movie) => (
                <li key={movie.id}>
                  <Link
                    to={`/detail/${movie.id}`}
                    className="group flex items-center gap-3 rounded-md py-2 transition-colors hover:bg-background-100"
                  >
                    <div className="h-12 w-9 flex-none overflow-hidden rounded bg-background-200">
                      <img
                        src={movie.cover}
                        alt={`${movie.title}封面`}
                        className="h-full w-full object-cover object-top"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate text-sm font-medium text-foreground-900 group-hover:text-primary-700">
                        {movie.title}
                      </h3>
                      <div className="mt-0.5 flex items-center gap-2 text-[11px] text-foreground-500">
                        <span className="flex items-center gap-0.5 text-accent-600">
                          <i className="ri-star-fill" />
                          {movie.rating.toFixed(1)}
                        </span>
                        <span className="flex items-center gap-0.5">
                          <i className="ri-eye-line" />
                          {formatViews(movie.views)}
                        </span>
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ol>
          </div>
        </aside>
      </div>
    </div>
  );
}